/**
 * The open claims behind a terminated person's counts (Terminated worklists page).
 *
 * The open pile at the worklist snapshot — FactOpenClaims — not Claim detail's billed
 * claims. Shayna asked (2026-10-01) to click a count and see the claims; Claim detail
 * could not answer it: the person with the largest pile has thousands of open claims
 * and no billed ones in a month they were paid.
 */
import type { EmployeeRow, TerminatedClaims, TerminatedWorklistRow } from './types'

export const UNASSIGNED = 'Unassigned'

/** The lines a person's total splits into; each one opens its claims. */
export type ClaimScope =
  | { kind: 'all' }
  | { kind: 'worklist'; name: string }
  | { kind: 'unassigned' }
  | { kind: 'other' }

export interface OpenClaim {
  claimId: string
  visitId: string | null
  worklist: string
  facility: string
  payerType: string
  payer: string
  claimType: string
  dos: string | null
  billDate: string | null
  lastTouch: string | null
  /** AGE_BUCKETS key: neverWorked / workedLast14 / worked15to30 / workedOver30. */
  age: string
  remaining: number | null
}

/** The open pile's buckets, worded as on the person sidebar. */
export const AGE_LABEL: Record<string, string> = {
  neverWorked: 'Never worked',
  // One label for both within-30-day buckets, as on the person page (#893).
  workedLast14: 'Last 30 days',
  worked15to30: 'Last 30 days',
  workedOver30: 'Over 30 days ago',
}

/** Sort order for the age column: never worked first, then oldest touch first. */
export const AGE_ORDER: Record<string, number> = {
  neverWorked: 0,
  workedOver30: 1,
  worked15to30: 2,
  workedLast14: 3,
}

/**
 * The visit in a claim id, the part after the dash ("273-4719298" → "4719298"): the
 * fallback when a row's VisitID did not come through. On the 2026-09 and 2026-10
 * snapshots every open claim's VisitID was exactly this suffix.
 */
export function visitOfClaim(claimId: string): string | null {
  const dash = claimId.indexOf('-')
  return dash > 0 && dash < claimId.length - 1 ? claimId.slice(dash + 1) : null
}

export function decodeClaims(c: TerminatedClaims): OpenClaim[] {
  const { dict: d, cols } = c
  /** Column `k`'s value at row `i`, looked up in its dictionary; '' if out of range. */
  const at = (k: keyof TerminatedClaims['dict'], i: number) =>
    d[k][cols[k][i] ?? -1] ?? ''
  const out: OpenClaim[] = []
  for (let i = 0; i < c.n; i++) {
    out.push({
      claimId: cols.c[i] ?? '',
      visitId: cols.v[i] ?? visitOfClaim(cols.c[i] ?? ''),
      worklist: at('wl', i),
      facility: at('f', i),
      payerType: at('p', i),
      payer: at('pn', i),
      claimType: at('ty', i),
      dos: at('dos', i) || null,
      billDate: at('bill', i) || null,
      lastTouch: at('lt', i) || null,
      age: at('age', i),
      remaining: cols.rem[i] ?? null,
    })
  }
  return out
}

/** The claims on one of a person's lines. */
export function claimsIn(
  row: TerminatedWorklistRow,
  claims: OpenClaim[],
  scope: ClaimScope,
): OpenClaim[] {
  if (scope.kind === 'all') return claims
  if (scope.kind === 'worklist') return claims.filter((c) => c.worklist === scope.name)
  if (scope.kind === 'unassigned') return claims.filter((c) => c.worklist === UNASSIGNED)
  const mine = new Set(row.worklists.map((w) => w.name))
  return claims.filter((c) => c.worklist !== UNASSIGNED && !mine.has(c.worklist))
}

export const scopeLabel = (scope: ClaimScope) =>
  scope.kind === 'all'
    ? 'All open claims'
    : scope.kind === 'worklist'
      ? scope.name
      : scope.kind === 'unassigned'
        ? UNASSIGNED
        : 'Other worklists'

export const sameScope = (a: ClaimScope, b: ClaimScope) =>
  a.kind === b.kind && (a.kind !== 'worklist' || a.name === (b as { name: string }).name)

/**
 * The person's Paylocity ID: the row's own, else (older bundle) the people list's by
 * FCCKey, else blank. Never the FCCKey — the export column is named Paylocity ID.
 */
export function paylocityOf(row: TerminatedWorklistRow, employees: readonly EmployeeRow[]): string {
  if (row.paylocityId !== undefined) return row.paylocityId ?? ''
  const keys = new Set(row.fccKeys)
  const ids = new Set(
    employees
      .filter((e) => e.fccKey && keys.has(e.fccKey) && e.paylocityId)
      .map((e) => e.paylocityId as string),
  )
  // Two IDs for one person is a question for the data, not a pick for the page.
  return ids.size === 1 ? ([...ids][0] ?? '') : ''
}
